/* ==========================================================================
   Carousel behaviour — geometry, keyboard, and open/close.

   What JavaScript is responsible for here is deliberately small:
     1. measuring the ring radius for the number of books present
     2. keyboard control (arrows rotate, Enter opens, Escape closes)
     3. toggling the open class on touch, where there is no hover
     4. handing the ring back to its own animation once the visitor stops

   Rotation, depth, the page turn and the spread cycling are all CSS. Nothing
   in this file runs per animation frame.

   RESUMING AFTER MANUAL CONTROL
   Arrow keys and the prev/next buttons put the ring in manual mode, which
   used to be a one-way door: the spin never came back. It now resumes when
   the visitor stops interacting, and immediately when the section scrolls
   out of view.

   Resuming sets --lk-phase to the point the ring was left at, and both the
   ring and the per-book depth animations read that same variable, so the
   spin picks up from the current angle with the depth cue still locked to
   it, instead of snapping to wherever a free-running clock had got to.

   Without JavaScript the ring still renders at its default radius, still
   rotates, still opens on hover and focus, and every cover is still a link to
   its product page.
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobile = window.matchMedia('(max-width: 749px)');

  var IDLE_MS = 6000; // quiet time before the ring takes itself back

  function setupCarousel(root) {
    var carousel = root.querySelector('[data-lk-carousel]');
    if (!carousel || carousel.dataset.lkReady === 'true') return;
    carousel.dataset.lkReady = 'true';

    var ring = carousel.querySelector('[data-lk-ring]');
    var items = Array.prototype.slice.call(carousel.querySelectorAll('[data-lk-item]'));
    if (!ring || !items.length) return;

    var count = items.length;
    var step = 360 / count;
    var angle = 0;
    var focusIndex = 0;
    var idleTimer = null;

    /* --- 1. Radius -----------------------------------------------------
       The ring's radius has to grow with the number of covers, or they
       overlap. r = (w / 2) / tan(pi / n), plus breathing room. */
    function measure() {
      if (mobile.matches) {
        ring.style.removeProperty('--lk-radius');
        return;
      }
      var width = items[0].getBoundingClientRect().width || 190;
      var radius = width / 2 / Math.tan(Math.PI / count) + 26;
      carousel.style.setProperty('--lk-radius', Math.round(radius) + 'px');
    }

    /* --- 2. Open and close ---------------------------------------------- */
    /* An open book is a still spread: the cover swings back to reveal the
       first interior page on its underside, and the second interior page sits
       alongside it. Nothing cycles. Closing swings the cover shut and the
       book shows its artwork again. */
    function close(book) {
      if (!book) return;
      book.classList.remove('is-open');
    }

    function closeAll() {
      items.forEach(function (item) {
        close(item.querySelector('.lk-book'));
      });
    }

    function open(index) {
      closeAll();
      var book = items[index] && items[index].querySelector('.lk-book');
      if (!book) return;
      book.classList.add('is-open');
    }

    /* --- 3. Manual rotation -------------------------------------------- */
    function spinSeconds() {
      var raw = getComputedStyle(carousel).getPropertyValue('--lk-carousel-spin');
      var n = parseFloat(raw);
      return isNaN(n) || n <= 0 ? 52 : n;
    }

    function rotateTo(index) {
      focusIndex = (index + count) % count;
      angle = -focusIndex * step;
      ring.classList.add('is-manual');
      ring.style.setProperty('--lk-angle', angle);
      open(focusIndex);

      // Move focus with the ring so keyboard and screen reader stay in step.
      var link = items[focusIndex].querySelector('a');
      if (link) link.focus({ preventScroll: true });

      scheduleResume();
    }

    function resumeAuto() {
      window.clearTimeout(idleTimer);
      closeAll();

      if (!ring.classList.contains('is-manual')) return;

      // Pick the spin back up from the angle we're actually sitting at.
      var total = spinSeconds();
      var travelled = (((-angle) % 360) + 360) % 360;
      var phase = -(travelled / 360) * total;
      carousel.style.setProperty('--lk-phase', phase.toFixed(3) + 's');

      ring.classList.remove('is-manual');
    }

    // Never yank a book shut while it's being read: if the pointer is still
    // on the carousel or focus is inside it, wait another interval.
    function scheduleResume() {
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(function () {
        var busy =
          (carousel.matches && carousel.matches(':hover')) ||
          carousel.contains(document.activeElement);
        if (busy) {
          scheduleResume();
          return;
        }
        resumeAuto();
      }, IDLE_MS);
    }

    carousel.addEventListener('keydown', function (event) {
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          rotateTo(focusIndex + 1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          rotateTo(focusIndex - 1);
          break;
        case 'Enter':
          if (event.target.closest('[data-lk-item]')) {
            // Enter on a focused cover opens the preview rather than
            // navigating, so the flip is reachable without a mouse.
            var item = event.target.closest('[data-lk-item]');
            var index = items.indexOf(item);
            if (index > -1 && !event.target.closest('.lk-book__panel')) {
              event.preventDefault();
              rotateTo(index);
            }
          }
          break;
        case 'Escape':
          event.preventDefault();
          resumeAuto();
          if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
          }
          break;
        default:
          break;
      }
    });

    // Track which cover the user tabbed to, so arrows continue from there.
    items.forEach(function (item, index) {
      item.addEventListener('focusin', function () {
        focusIndex = index;
      });
    });

    /* --- 4. Touch: no hover exists, so tapping the preview button opens -- */
    carousel.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-lk-preview]');
      if (!toggle) return;
      event.preventDefault();

      var item = toggle.closest('[data-lk-item]');
      var book = item && item.querySelector('.lk-book');
      if (!book) return;

      var isOpen = book.classList.contains('is-open');
      closeAll();
      items.forEach(function (other) {
        var btn = other.querySelector('[data-lk-preview]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });

      if (!isOpen) {
        book.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });

    /* --- 5. Prev / next buttons ----------------------------------------- */
    var prev = carousel.querySelector('[data-lk-prev]');
    var next = carousel.querySelector('[data-lk-next]');

    if (prev) {
      prev.addEventListener('click', function () {
        if (mobile.matches) {
          ring.scrollBy({ left: -ring.clientWidth * 0.7, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        } else {
          rotateTo(focusIndex - 1);
        }
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        if (mobile.matches) {
          ring.scrollBy({ left: ring.clientWidth * 0.7, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        } else {
          rotateTo(focusIndex + 1);
        }
      });
    }

    /* --- 6. Out of sight, hand the ring back ---------------------------- */
    if ('IntersectionObserver' in window) {
      var seen = new IntersectionObserver(
        function (entries) {
          if (!entries[0].isIntersecting) resumeAuto();
        },
        { threshold: 0.05 }
      );
      seen.observe(carousel);
    }

    measure();

    var resizeTimer = null;
    window.addEventListener(
      'resize',
      function () {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(measure, 150);
      },
      { passive: true }
    );
  }

  function init(root) {
    var scope = root || document;
    if (scope.querySelector && scope.querySelector('[data-lk-carousel]')) {
      setupCarousel(scope);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
